import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Snackbar,
  Alert,
  Tabs,
  Tab,
  Grid,
  useMediaQuery,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Vehicle, MileageReport } from '../../types/vehicle';
import { MaintenanceRecord } from '../../types/maintenance';
import { FuelConsumption } from '../../types/fuelConsumption';
import { fuelConsumptionService } from '../../services/fuelConsumptionService';
import { useMaintenancePlan } from '../../hooks/useMaintenancePlan';
import { useAuth } from '../../contexts/AuthContext';
import ImageViewer from '../common/ImageViewer';
import VehicleSummaryPanel from './VehicleSummaryPanel';
import VehicleTabContent from './VehicleTabContent';
import VehicleDialogs from './VehicleDialogs';
import { FuelEditDialog } from '../fuel/FuelEditDialog';

interface VehicleDetailsProps {
  vehicle: Vehicle;
  mileageReports: MileageReport[];
  reportUsers: Record<string, { displayName: string }>;
  onUpdate: () => void;
  onEditVehicle: (vehicle: Vehicle) => void;
  onDeleteVehicle: (vehicleId: string) => void;
}

const VehicleDetails: React.FC<VehicleDetailsProps> = ({
  vehicle,
  mileageReports,
  reportUsers,
  onUpdate,
  onEditVehicle,
  onDeleteVehicle
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { user } = useAuth();
  const { checkAdminRole } = useMaintenancePlan(vehicle.id);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(false);

  // Vérifier le rôle admin au chargement du composant
  useEffect(() => {
    const checkAdmin = async () => {
      setLoading(true);
      try {
        const isAdminUser = await checkAdminRole(vehicle.organizationId);
        setIsAdmin(isAdminUser);
      } catch (error) {
        console.error('Erreur lors de la vérification du rôle admin:', error);
        setIsAdmin(false);
      } finally {
        setLoading(false);
      }
    };
    checkAdmin();
  }, [checkAdminRole, vehicle.organizationId]);

  // États pour la gestion des dialogues et des données
  const [activeTab, setActiveTab] = useState(0);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [showMileageDialog, setShowMileageDialog] = useState(false);
  const [showMaintenanceForm, setShowMaintenanceForm] = useState(false);
  const [showMaintenancePlanForm, setShowMaintenancePlanForm] = useState(false);
  const [selectedMaintenance, setSelectedMaintenance] = useState<MaintenanceRecord | undefined>(undefined);
  const [fuelConsumptions, setFuelConsumptions] = useState<FuelConsumption[]>([]);
  const [isLoadingFuel, setIsLoadingFuel] = useState(false);
  const [showFuelEntryDialog, setShowFuelEntryDialog] = useState(false);
  const [showFuelEditDialog, setShowFuelEditDialog] = useState(false);
  const [selectedFuelRecord, setSelectedFuelRecord] = useState<FuelConsumption | null>(null);
  const [fuelToDelete, setFuelToDelete] = useState<FuelConsumption | null>(null);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({
    open: false,
    message: '',
    severity: 'success'
  });

  // Load fuel consumptions
  const loadFuelConsumptions = async () => {
    if (!isLoadingFuel) {
      setIsLoadingFuel(true);
      try {
        const records = await fuelConsumptionService.getByVehicle(vehicle.id);
        setFuelConsumptions(records);
      } catch (error) {
        console.error('Error loading fuel consumptions:', error);
        setSnackbar({
          open: true,
          message: t('fuelReceipt.errors.loadFailed'),
          severity: 'error'
        });
      } finally {
        setIsLoadingFuel(false);
      }
    }
  };

  // Load data when component mounts or vehicle changes
  React.useEffect(() => {
    loadFuelConsumptions();
  }, [vehicle.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDocumentUpload = async () => {
    onUpdate();
    setShowUploadDialog(false);
  };

  const handleDeleteDocument = async (url: string) => {
    // TODO: Implement document deletion
    console.log('Delete document:', url);
  };

  const handleEditFuel = (record: FuelConsumption) => {
    setSelectedFuelRecord(record);
    setShowFuelEditDialog(true);
  };

  const handleDeleteFuel = (record: FuelConsumption) => {
    setFuelToDelete(record);
  };

  const handleConfirmDeleteFuel = async () => {
    if (!fuelToDelete) return;

    try {
      await fuelConsumptionService.delete(fuelToDelete.id);
      setSnackbar({
        open: true,
        message: t('fuelReceipt.success.deleted'),
        severity: 'success'
      });
      loadFuelConsumptions();
    } catch (error) {
      console.error('Error deleting fuel consumption:', error);
      setSnackbar({
        open: true,
        message: t('fuelReceipt.errors.deleteFailed'),
        severity: 'error'
      });
    } finally {
      setFuelToDelete(null);
    }
  };

  const handleFuelEditSuccess = () => {
    setSnackbar({
      open: true,
      message: t('fuelReceipt.success.updated'),
      severity: 'success'
    });
    loadFuelConsumptions();
  };

  return (
    <Grid container spacing={2}>
      {/* Summary Panel */}
      <Grid item xs={12} md={3}>
        <VehicleSummaryPanel
          vehicle={vehicle}
          reportUsers={reportUsers}
          onEditVehicle={onEditVehicle}
          onDeleteVehicle={onDeleteVehicle}
        />
      </Grid>

      {/* Main Content */}
      <Grid item xs={12} md={9}>
        <Box>
          {/* Vehicle Assignment Info */}
          <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
            <Button
              variant="outlined"
              size="small"
              onClick={() => setShowAssignDialog(true)}
            >
              {vehicle.currentAssignment ? t('vehicle.details.buttons.reassign') : t('vehicle.details.buttons.assign')}
            </Button>
          </Box>

          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs
              value={activeTab}
              onChange={(_, newValue) => setActiveTab(newValue)}
              aria-label="vehicle details tabs"
              variant={isMobile ? "scrollable" : "standard"}
              scrollButtons={isMobile ? "auto" : false}
            >
              <Tab label={t('vehicle.details.tabs.maintenance')} />
              <Tab label={t('vehicle.details.tabs.maintenancePlans')} />
              <Tab label={t('vehicle.details.tabs.fuel')} />
              <Tab label={t('vehicle.details.tabs.technicalInspection')} />
              <Tab label={t('vehicle.details.tabs.alerts')} />
              <Tab label={t('vehicle.details.tabs.mileageHistory')} />
              <Tab label={t('vehicle.details.tabs.documents')} />
            </Tabs>
          </Box>

          <VehicleTabContent
            vehicle={vehicle}
            activeTab={activeTab}
            mileageReports={mileageReports}
            fuelConsumptions={fuelConsumptions}
            reportUsers={reportUsers}
            isLoadingFuel={isLoadingFuel}
            isMobile={isMobile}
            isAdmin={isAdmin}
            loading={loading}
            currentUserId={user?.id}
            onShowMaintenanceForm={() => setShowMaintenanceForm(true)}
            onShowMaintenancePlanForm={() => setShowMaintenancePlanForm(true)}
            onShowMileageDialog={() => setShowMileageDialog(true)}
            onShowFuelEntryDialog={() => setShowFuelEntryDialog(true)}
            onShowUploadDialog={() => setShowUploadDialog(true)}
            onUpdate={onUpdate}
            onEditMaintenance={(record) => {
              setSelectedMaintenance(record);
              setShowMaintenanceForm(true);
            }}
            onEditFuel={handleEditFuel}
            onDeleteFuel={handleDeleteFuel}
            onImageClick={(url) => setSelectedImage(url)}
            onDeleteDocument={handleDeleteDocument}
          />
        </Box>
      </Grid>

      {/* Dialogs */}
      <VehicleDialogs
        vehicle={vehicle}
        showUploadDialog={showUploadDialog}
        showAssignDialog={showAssignDialog}
        showMileageDialog={showMileageDialog}
        showMaintenanceForm={showMaintenanceForm}
        showMaintenancePlanForm={showMaintenancePlanForm}
        showFuelEntryDialog={showFuelEntryDialog}
        selectedMaintenance={selectedMaintenance}
        loading={loading}
        onCloseUpload={() => setShowUploadDialog(false)}
        onCloseAssign={() => setShowAssignDialog(false)}
        onCloseMileage={() => setShowMileageDialog(false)}
        onCloseMaintenanceForm={() => {
          setShowMaintenanceForm(false);
          setSelectedMaintenance(undefined);
        }}
        onClosePlanForm={() => setShowMaintenancePlanForm(false)}
        onCloseFuelEntry={() => setShowFuelEntryDialog(false)}
        onUpdate={onUpdate}
        onUploadComplete={handleDocumentUpload}
        setSnackbar={setSnackbar}
      />

      {/* Error Snackbar */}
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
      >
        <Alert 
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Image Viewer */}
      {selectedImage && (
        <ImageViewer
          open={!!selectedImage}
          onClose={() => setSelectedImage(null)}
          imageUrl={selectedImage}
        />
      )}

      {/* Fuel Edit Dialog */}
      <FuelEditDialog
        open={showFuelEditDialog}
        fuelRecord={selectedFuelRecord}
        onClose={() => {
          setShowFuelEditDialog(false);
          setSelectedFuelRecord(null);
        }}
        onSuccess={handleFuelEditSuccess}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={!!fuelToDelete}
        onClose={() => setFuelToDelete(null)}
      >
        <DialogTitle>{t('fuelReceipt.delete.title')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('fuelReceipt.delete.confirmMessage')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFuelToDelete(null)}>
            {t('common.cancel')}
          </Button>
          <Button onClick={handleConfirmDeleteFuel} color="error" variant="contained">
            {t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
};

export default VehicleDetails;
